import React, { useMemo } from 'react';
import {
  ReportColumn,
  DEFAULT_SHOW,
  ReportFilter,
  ACTION_COLUMN,
} from 'components/modules/report/report-types';
import { Model } from 'util/backendapi/models/api.interfaces';
import { t, Trans } from '@lingui/macro';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { ReportFiltersBlock } from 'components/modules/report/filter/ReportFiltersBlock';
import ActionBlock from 'components/base/actionblock/actionblock';
import { ReportTable } from 'components/modules/report/table/ReportTable';
import { ReportStateProps } from 'hooks/use-report-state';
import { useHistory } from 'react-router';
import Button from 'components/base/button/button';
import { formatDatetimeForDisplay } from 'util/dates';
import { TransEnum } from 'components/base/i18n/TransEnum';
import { IntervalDisplay } from 'components/base/i18n/IntervalDisplay';
import { DMSLink } from 'components/base/link/DMSLink';
import { Icon } from 'components/base/icon/icon';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';

export interface TimeLagMonitorListViewProps {
  deleteTimeLagMonitor: (id: number) => Promise<any>;
  reportProps: ReportStateProps<Model.ReportsTimeLagMonitor>;
}

export const timeLagMonitorListColumns: (props: {
  refreshList: () => void;
  deleteTimeLagMonitor: TimeLagMonitorListViewProps['deleteTimeLagMonitor'];
}) => ReportColumn<Model.ReportsTimeLagMonitor>[] = ({
  refreshList,
  deleteTimeLagMonitor,
}) => [
  {
    label: <Trans>Observation point A</Trans>,
    name: 'observation_point_a__code',
    visibility: DEFAULT_SHOW,
    accessor: ({ observation_point_a__code: code }) => code,
  },
  {
    label: <Trans>Observation point B</Trans>,
    name: 'observation_point_b__code',
    visibility: DEFAULT_SHOW,
    accessor: ({ observation_point_b__code: code }) => code,
  },
  {
    label: <Trans>Area</Trans>,
    name: 'area__code',
    visibility: DEFAULT_SHOW,
    accessor: ({ area__code: code }) => code,
  },
  {
    label: <Trans>Start date</Trans>,
    name: 'start_datetime',
    additionalFields: ['area__time_zone__name'],
    visibility: DEFAULT_SHOW,
    accessor: ({ start_datetime, area__time_zone__name: time_zone }) =>
      formatDatetimeForDisplay(start_datetime, time_zone),
  },
  {
    label: <Trans>Cycle length</Trans>,
    name: 'cycle_length',
    visibility: DEFAULT_SHOW,
    accessor: ({ cycle_length }) => <IntervalDisplay value={cycle_length} />,
  },
  {
    label: <Trans>Resample frequency</Trans>,
    name: 'resample_frequency',
    visibility: DEFAULT_SHOW,
    accessor: ({ resample_frequency }) => (
      <IntervalDisplay value={resample_frequency} />
    ),
  },
  {
    label: <Trans>Window length</Trans>,
    name: 'window_length',
    visibility: DEFAULT_SHOW,
    accessor: ({ window_length }) => <IntervalDisplay value={window_length} />,
  },
  {
    label: <Trans>Mode</Trans>,
    name: 'mode',
    visibility: DEFAULT_SHOW,
    accessor: ({ mode }) => (
      <TransEnum enum="TimeLagMonitor_MODE" value={mode}></TransEnum>
    ),
  },
  {
    label: <Trans>Output observation point</Trans>,
    name: 'output_observation_point__code',
    visibility: DEFAULT_SHOW,
    accessor: ({ output_observation_point__code: code }) => code,
  },
  {
    ...ACTION_COLUMN,
    additionalFields: [
      'id',
      'observation_point_a__code',
      'observation_point_b__code',
      'output_observation_point__code',
    ],
    accessor: ({
      id,
      observation_point_a__code,
      observation_point_b__code,
      output_observation_point__code,
    }) => (
      <>
        <DMSLink to={`/time-lag-monitors/${id}`}>
          <Icon type="icon-edit" title={t`Edit`} />
        </DMSLink>
        <ButtonShowConfirmation
          name="delete-time-lag-monitor"
          title={t`Delete`}
          iconType="icon-circle-minus"
          className="btn-link-panel"
          content={
            <Trans>
              Are you sure you want to delete the time lag for{' '}
              <strong>{observation_point_a__code}</strong> and{' '}
              <strong>{observation_point_b__code}</strong>? Once deleted no
              further readings will be created on{' '}
              <strong>{output_observation_point__code}</strong> for these time
              lag settings. This action is not reversible.
            </Trans>
          }
          okBtnText={<Trans>Yes, delete</Trans>}
          onConfirm={async function () {
            await deleteTimeLagMonitor(id);
            refreshList();
          }}
        />
      </>
    ),
  },
];

export const timeLagMonitorListFilters: ReportFilter[] = [];

export function TimeLagMonitorListView(props: TimeLagMonitorListViewProps) {
  const { reportProps, deleteTimeLagMonitor } = props;
  const filters = timeLagMonitorListFilters;

  const { refreshList } = reportProps;

  const columns = useMemo(
    () => timeLagMonitorListColumns({ refreshList, deleteTimeLagMonitor }),
    [refreshList, deleteTimeLagMonitor]
  );

  const history = useHistory();

  return (
    <PageStandard
      name="time-lag-monitor-report"
      header={<Trans>Maintain time lag monitors</Trans>}
    >
      <div className="page-content-header-filters-actions">
        <ReportFiltersBlock
          filtersBackend={reportProps.reportInfo?.filters}
          filtersFrontend={filters}
          isExportMode={reportProps.isExportMode}
        />
      </div>
      <div className="filtered-table-wrapper">
        <ActionBlock className="text-right">
          <Button
            iconType="icon-plus"
            onClick={() => {
              history.push('/time-lag-monitors/new');
            }}
          >
            <Trans>Create time lag monitor</Trans>
          </Button>
        </ActionBlock>
        <ReportTable<Model.ReportsTimeLagMonitor>
          {...reportProps}
          columns={columns}
          filters={filters}
        />
      </div>
    </PageStandard>
  );
}
